// src/subscreens/mechanicadmin/requests/MaintenanceRequests.jsx

import React, { useState, useEffect, useContext } from "react";
import { Row, Col } from "react-bootstrap";
import {
  FiTool, FiClock, FiCheckCircle, FiTrash2,
  FiEye, FiX, FiAlertCircle, FiMapPin,
  FiCalendar, FiUser, FiTruck, FiFileText,
  FiMoreVertical, FiEdit
} from "react-icons/fi";
import { MdBuild } from "react-icons/md";
import MechanicMasterLayout from "../../../layout/mechanic/MechanicMasterLayout";
import "../../../assets/css/mechanic/maintenancerequest/MaintenanceRequest.css";
import AppContext from "../../../context/AppContext";
import axios from "axios";
import Swal from "sweetalert2";
 
const STATUS_CONFIG = {
  pending: { label: "Pending", color: "#FDB714", bg: "#FDB71420" },
  active: { label: "Active", color: "#007bff", bg: "#007bff20" },
  completed: { label: "Completed", color: "#28a745", bg: "#28a74520" },
  cancelled: { label: "Cancelled", color: "#e8590c", bg: "#e8590c20" },
  default: { label: "Pending", color: "#FDB714", bg: "#FDB71420" },
};

const getSC = (status) => STATUS_CONFIG[status] || STATUS_CONFIG["default"];

const getRequester = (req, baseUrlForFile) => {
  const isCustomer = !!req.srcustomer_id;
  const source = isCustomer ? req.srcustomer : req.srrentaladmin;

  // ✅ build the image URL properly using baseUrlForFile
  const rawImage = source?.image || source?.profileimage || null;
  const imageUrl = rawImage ? `${baseUrlForFile}/${rawImage}` : null;

  return {
    fullname: source?.fullname || source?.name || "N/A",
    email: source?.email || "N/A",
    image: imageUrl,
    type: isCustomer ? "Customer" : "Rental Admin",
  };
};

export default function MaintenanceRequests() {
  // ✅ get baseUrlForFile from context too
  const { baseUrl, baseUrlForFile, loggedInMechanic, authToken } = useContext(AppContext);

  const [requests, setRequests] = useState([]);
  const [viewModal, setViewModal] = useState(null);
  const [statusModal, setStatusModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [commissionRate, setCommissionRate] = useState(0);
  const [canAcceptNewJobs, setCanAcceptNewJobs] = useState(true);

  const viewMyMaintenanceRequests = () => {
    if (!loggedInMechanic) return;
    axios.post(
      `${baseUrl}/mechanicadmin/maintenancerequests/viewMyMaintenanceRequests`,
      {
        srmechanicadmin_id: loggedInMechanic.id,
      }, {
      headers: { Authorization: `Bearer ${authToken}` }
    }
    )
      .then((res) => {
        if (res.data.success) {
          setRequests(res.data.request);
          setWalletBalance(res.data.walletBalance ?? 0);
          setCommissionRate(res.data.commissionRate ?? 0);
          setCanAcceptNewJobs(res.data.canAcceptNewJobs ?? true);
        }
      })
      .catch((err) => console.log("API Error:", err));
  };

  useEffect(() => {
    if (loggedInMechanic?.id) viewMyMaintenanceRequests();
  }, [loggedInMechanic]);

  useEffect(() => {
    const close = (e) => {
      if (!e.target.closest(".mr-actions-wrapper")) setActiveMenu(null);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  // ── Stats ──
  const total = requests.length;
  const active = requests.filter((r) => r.status === "active").length;
  const pending = requests.filter((r) => r.status === "pending").length;
  const completed = requests.filter((r) => r.status === "completed").length;

  const statsData = [
    { id: 1, icon: <MdBuild />, count: total, label: "Total Requests" },
    { id: 2, icon: <FiAlertCircle />, count: active, label: "Active" },
    { id: 3, icon: <FiClock />, count: pending, label: "Pending" },
    { id: 4, icon: <FiCheckCircle />, count: completed, label: "Completed" },
  ];

  // ── Status Update ──
  const handleStatusUpdate = (id, newStatus) => {
    const formData = new FormData();
    formData.append("id", id);
    formData.append("status", newStatus);
    axios.post(
      `${baseUrl}/mechanicadmin/maintainanceRequests/updateStatus`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`
        }
      }
    )
      .then((res) => {
        if (res.data.success) {
          setStatusModal(null);
          Swal.fire({ title: "Wow", icon: "success", confirmButtonText: "Okay" });
          viewMyMaintenanceRequests();
        } else if (res.data.walletTooLow) {
          setStatusModal(null);
          Swal.fire({
            title: "Wallet Balance Too Low",
            text: res.data.message,
            icon: "warning",
            confirmButtonText: "Top Up Wallet",
            confirmButtonColor: "#FDB714",
            showCancelButton: true,
            cancelButtonText: "Not Now",
          }).then((result) => {
            if (result.isConfirmed) {
              window.location.href = "/mechanic-admin/earnings";
            }
          });
        } else {
          setStatusModal(null);
          Swal.fire({ title: "Could not update", text: res.data.message, icon: "error" });
        }
      })
      .catch((err) => console.log(err));
  };

  // ── Cancel Booking — no advance payment is involved, so it cancels immediately ──
  const handleCancelBooking = (req) => {
    setActiveMenu(null);
    Swal.fire({
      icon: "warning",
      title: "Cancel This Booking?",
      text: "Are you sure you want to cancel this booking? This action cannot be undone.",
      showCancelButton: true,
      confirmButtonColor: "#e8590c",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Yes, Cancel Booking",
      cancelButtonText: "No, Keep Booking",
    }).then((result) => {
      if (!result.isConfirmed) return;

      axios
        .post(
          `${baseUrl}/mechanicadmin/maintainanceRequests/cancelBooking`,
          {
            booking_id: req.id,
            srmechanicadmin_id: loggedInMechanic.id,
          },
          { headers: { Authorization: `Bearer ${authToken}` } },
        )
        .then((res) => {
          if (!res.data.success) {
            Swal.fire({ icon: "error", title: "Could not cancel", text: res.data.message });
            return;
          }
          Swal.fire({ icon: "success", title: "Booking Cancelled", confirmButtonColor: "#FDB714" });
          viewMyMaintenanceRequests();
        })
        .catch((err) => {
          console.log(err);
          Swal.fire({ icon: "error", title: "Error", text: "Something went wrong while cancelling the booking." });
        });
    });
  };

  const handleDelete = (id) => {
    setRequests((prev) => prev.filter((r) => r.id !== id));
    setDeleteConfirm(null);
  };

  // ── Payment Badge ──
  const renderPaymentBadge = (payment) => {
    const isPaid = String(payment).toLowerCase() === "paid";
    return (
      <span className={`mr-payment-badge ${isPaid ? "mr-payment-paid" : "mr-payment-unpaid"}`}>
        {isPaid ? "Paid" : "Unpaid"}
      </span>
    );
  };

  return (
    <MechanicMasterLayout>
      <div className="mr-page">

        {/* ── Page Header ── */}
        <div className="mr-page-header">
          <div>
            <h2 className="mr-page-title">Maintenance Requests</h2>
            <p className="mr-page-sub">Manage incoming service requests from farmers</p>
          </div>
        </div>

        {/* ── Low Wallet Balance Warning ── */}
        {!canAcceptNewJobs && (
          <div
            style={{
              background: "#FFF3E0",
              border: "1px solid #FDB714",
              borderRadius: 10,
              padding: "12px 18px",
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            <span style={{ fontSize: 14, color: "#7a4a00" }}>
              <FiAlertCircle style={{ marginRight: 6, verticalAlign: "middle" }} />
              <strong>Wallet Balance: Rs. {walletBalance}</strong> — this is too low
              to accept new jobs (a Rs. {commissionRate} commission is deducted per
              completed job). Please top up your wallet to accept new jobs.
            </span>
            <a
              href="/mechanic-admin/earnings"
              style={{
                background: "#FDB714",
                color: "#fff",
                padding: "6px 16px",
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 600,
                textDecoration: "none",
                whiteSpace: "nowrap",
              }}
            >
              Top Up Wallet
            </a>
          </div>
        )}

        {/* ── Stat Cards ── */}
        <div className="mr-stats-container">
          <Row className="g-4">
            {statsData.map((card) => (
              <Col key={card.id} xl={3} lg={3} md={6} sm={12}>
                <div className="mr-stat-card" style={{ borderLeft: "4px solid #FDB714" }}>
                  <div className="mr-stat-icon">{card.icon}</div>
                  <div className="mr-stat-content">
                    <h3>{card.count}</h3>
                    <p>{card.label}</p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </div>

        {/* ── Table ── */}
        <div className="mr-table-section">
          <div className="mr-table-header">
            <h3 className="mr-table-title">All Maintenance Requests</h3>
            <span className="mr-table-count">{requests.length} requests</span>
          </div>

          <div className="mr-table-wrapper">
            <table className="mr-table">
              <thead>
                <tr>
                  {/* ✅ REQUEST ID and PAYMENT columns removed */}
                  <th>FULL NAME</th>
                  <th>MACHINERY TYPE</th>
                  <th>PREFERRED DATE</th>
                  <th>LOCATION</th>
                  <th>BOOKING FEE</th>
                  <th>STATUS</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {requests.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center", padding: "40px", color: "#888" }}>
                      No maintenance requests found
                    </td>
                  </tr>
                ) : (
                  requests.map((req) => {
                    const sc = getSC(req.status);
                    // ✅ pass baseUrlForFile
                    const requester = getRequester(req, baseUrlForFile);

                    return (
                      <tr key={req.id} className={req.status === "active" ? "mr-row-active" : ""}>

                        {/* ✅ Full Name — fixed layout */}
                        <td>
                          <div className="mr-td-farmer">
                            {requester.image ? (
                              <img
                                src={requester.image}
                                alt={requester.fullname}
                                className="mr-farmer-avatar-img"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <div className="mr-farmer-avatar">
                                {requester.fullname?.charAt(0)?.toUpperCase()}
                              </div>
                            )}
                            <div className="mr-farmer-info">
                              <span className="mr-farmer-name">{requester.fullname}</span>
                              <span className="mr-farmer-email">{requester.email}</span>
                              <span className="mr-farmer-type">{requester.type}</span>
                            </div>
                          </div>
                        </td>

                        {/* Machinery */}
                        <td>
                          <span className="mr-machine-label">
                            <FiTruck size={12} style={{ marginRight: 5, color: "#FDB714" }} />
                            {req.machinerytype}
                          </span>
                        </td>

                        {/* Date */}
                        <td>{req.preferreddate}</td>

                        {/* Location */}
                        <td className="mr-td-location">
                          <FiMapPin size={12} style={{ marginRight: 4, color: "#FDB714" }} />
                          {req.location}
                        </td>

                        {/* Booking Fee */}
                        <td className="mr-td-fee">Rs. {req.bookingfee}</td>

                        {/* Status */}
                        <td>
                          <span
                            className="mr-status-badge"
                            style={{ background: sc.bg, color: sc.color }}
                          >
                            {sc.label}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="mr-actions-cell">
                          <div className="mr-actions-wrapper">
                            <button
                              className="mr-menu-btn"
                              onClick={() => setActiveMenu(activeMenu === req.id ? null : req.id)}
                            >
                              <FiMoreVertical size={20} />
                            </button>

                            {activeMenu === req.id && (
                              <div className="mr-dropdown">
                                <button
                                  className="mr-drop-item mr-drop-view"
                                  onClick={() => { setViewModal(req); setActiveMenu(null); }}
                                >
                                  <FiEye size={14} /> View Detail
                                </button>
                                {!["cancelled", "completed"].includes(req.status) && (
                                  <>
                                    <button
                                      className="mr-drop-item mr-drop-update"
                                      disabled={req.status === "pending" && !canAcceptNewJobs}
                                      style={
                                        req.status === "pending" && !canAcceptNewJobs
                                          ? { opacity: 0.5, cursor: "not-allowed" }
                                          : undefined
                                      }
                                      title={
                                        req.status === "pending" && !canAcceptNewJobs
                                          ? "Top up your wallet to accept new jobs"
                                          : ""
                                      }
                                      onClick={() => {
                                        if (req.status === "pending" && !canAcceptNewJobs) return;
                                        setStatusModal(req);
                                        setActiveMenu(null);
                                      }}
                                    >
                                      <FiEdit size={14} />{" "}
                                      {req.status === "pending" && !canAcceptNewJobs
                                        ? "Update Status (Top up wallet)"
                                        : "Update Status"}
                                    </button>
                                    <button
                                      className="mr-drop-item"
                                      style={{ color: "#e8590c" }}
                                      onClick={() => handleCancelBooking(req)}
                                    >
                                      <FiTrash2 size={14} /> Cancel Booking
                                    </button>
                                  </>
                                )}
                                <button
                                  className="mr-drop-item mr-drop-delete"
                                  onClick={() => { setDeleteConfirm(req.id); setActiveMenu(null); }}
                                >
                                  <FiTrash2 size={14} /> Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </td>

                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ══ VIEW DETAIL MODAL ══ */}
        {viewModal && (() => {
          // ✅ pass baseUrlForFile in the modal too
          const requester = getRequester(viewModal, baseUrlForFile);
          return (
            <div className="mr-overlay" onClick={() => setViewModal(null)}>
              <div className="mr-modal" onClick={(e) => e.stopPropagation()}>

                <div className="mr-modal-header">
                  <div className="mr-modal-header-left">
                    <h5>Request Detail</h5>
                    <span>Full breakdown of this maintenance request</span>
                  </div>
                  <button className="mr-modal-close" onClick={() => setViewModal(null)}>
                    <FiX />
                  </button>
                </div>

                <div className="mr-modal-body">
                  <div className="mr-modal-status-bar">
                    <div className="mr-sb-left">
                      {requester.image ? (
                        <img
                          src={requester.image}
                          alt={requester.fullname}
                          className="mr-farmer-avatar-img"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="mr-farmer-avatar">
                          {requester.fullname?.charAt(0)?.toUpperCase()}
                        </div>
                      )}
                      <span className="mr-sb-id">
                        #{viewModal.id} — {requester.fullname} ({requester.type})
                      </span>
                      <span
                        className="mr-status-badge"
                        style={{
                          background: getSC(viewModal.status).bg,
                          color: getSC(viewModal.status).color,
                        }}
                      >
                        {getSC(viewModal.status).label}
                      </span>
                    </div>
                  </div>

                  <p className="mr-section-title">Request Information</p>
                  <div className="mr-info-grid">
                    <div className="mr-info-item">
                      <label>Full Name</label>
                      <span>{requester.fullname}</span>
                    </div>
                    <div className="mr-info-item">
                      <label>Email</label>
                      <span>{requester.email}</span>
                    </div>
                    <div className="mr-info-item">
                      <label>Requested By</label>
                      <span>{requester.type}</span>
                    </div>
                    <div className="mr-info-item">
                      <label>Machinery Type</label>
                      <span>{viewModal.machinerytype}</span>
                    </div>
                    <div className="mr-info-item">
                      <label>Preferred Date</label>
                      <span>{viewModal.preferreddate}</span>
                    </div>
                    <div className="mr-info-item">
                      <label>Location</label>
                      <span>{viewModal.location}</span>
                    </div>
                    <div className="mr-info-item">
                      <label>Booking Fee</label>
                      <span>Rs. {viewModal.bookingfee}</span>
                    </div>
                    {/* ✅ Payment field added to modal */}
                    <div className="mr-info-item">
                      <label>Payment</label>
                      <span>{renderPaymentBadge(viewModal.payment)}</span>
                    </div>
                  </div>

                  <p className="mr-section-title">Issue Description</p>
                  <div className="mr-notes-box">
                    {viewModal.descriptionissue || "No description provided"}
                  </div>
                </div>

                <div className="mr-modal-footer">
                  <button
                    className="mr-mf-btn mr-mf-update"
                    onClick={() => { setViewModal(null); setStatusModal(viewModal); }}
                  >
                    <FiEdit size={14} /> Update Status
                  </button>
                  <button className="mr-mf-btn mr-mf-close" onClick={() => setViewModal(null)}>
                    Close
                  </button>
                </div>

              </div>
            </div>
          );
        })()}

        {/* ══ UPDATE STATUS MODAL ══ */}
        {statusModal && (
          <div className="mr-overlay" onClick={() => setStatusModal(null)}>
            <div className="mr-modal mr-modal-sm" onClick={(e) => e.stopPropagation()}>

              <div className="mr-modal-header">
                <div className="mr-modal-header-left">
                  <h5>Update Status</h5>
                  <span>
                    {getRequester(statusModal, baseUrlForFile).fullname} — {statusModal.machinerytype}
                  </span>
                </div>
                <button className="mr-modal-close" onClick={() => setStatusModal(null)}>
                  <FiX />
                </button>
              </div>

              <div className="mr-modal-body">
                <p className="mr-status-prompt">
                  Current:&nbsp;
                  <span
                    className="mr-status-badge"
                    style={{
                      background: getSC(statusModal.status).bg,
                      color: getSC(statusModal.status).color,
                    }}
                  >
                    {getSC(statusModal.status).label}
                  </span>
                </p>
                {active >= 1 && statusModal.status !== "active" && (
                  <p className="mr-status-warning">
                    <FiAlertCircle size={13} /> Setting Active will move the current active request to Pending.
                  </p>
                )}
                <div className="mr-status-options">
                  <button
                    className={`mr-status-option ${statusModal.status === "active" ? "mr-status-current" : ""}`}
                    onClick={() => handleStatusUpdate(statusModal.id, "active")}
                    disabled={statusModal.status === "active"}
                  >
                    <FiAlertCircle size={18} />
                    <span>Active</span>
                    {statusModal.status === "active" && <span className="mr-current-tag">Current</span>}
                  </button>
                  <button
                    className={`mr-status-option mr-status-done ${statusModal.status === "completed" ? "mr-status-current" : ""}`}
                    onClick={() => handleStatusUpdate(statusModal.id, "completed")}
                    disabled={statusModal.status === "completed"}
                  >
                    <FiCheckCircle size={18} />
                    <span>Completed</span>
                    {statusModal.status === "completed" && <span className="mr-current-tag">Current</span>}
                  </button>
                </div>
              </div>

            </div>
          </div>
        )}

        {/* ══ DELETE CONFIRM ══ */}
        {deleteConfirm && (
          <div className="mr-overlay" onClick={() => setDeleteConfirm(null)}>
            <div className="mr-modal mr-modal-sm" onClick={(e) => e.stopPropagation()}>
              <div className="mr-delete-body">
                <div className="mr-delete-icon"><FiTrash2 size={28} /></div>
                <h3 className="mr-delete-title">Delete Request?</h3>
                <p className="mr-delete-desc">
                  This action cannot be undone.
                </p>
                <div className="mr-delete-actions">
                  <button className="mr-mf-btn mr-mf-close" onClick={() => setDeleteConfirm(null)}>
                    Cancel
                  </button>
                  <button className="mr-mf-btn mr-mf-delete" onClick={() => handleDelete(deleteConfirm)}>
                    <FiTrash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </MechanicMasterLayout>
  );
}